(function () {
  const SECTION_SELECTOR = '[data-advanced-announcement]';
  const instances = {};

  function AnnouncementSlider(section) {
    this.section = section;
    this.sliderEl = section.querySelector('[data-announcement-slider]');
    this.slides = [];
    this.allSlides = Array.prototype.slice.call(section.querySelectorAll('[data-announcement-slide]'));
    this.enableAutoplay = section.dataset.enableAutoplay === 'true';
    this.autoplayMs = Number(section.dataset.autoplayMs || 0);
    this.countryIso = String(section.dataset.countryIso || '').toLowerCase();
    this.detectedCountryHandle = String(section.dataset.countryIso || '').toUpperCase();
    this.activeIndex = 0;
    this.timer = null;
    this.isAnimating = false;
    this.transitionMs = 320;
    this.scrollPxPerSecond = 45;
    this.scrollCycleDelayMs = 800;
    this.scrollResumeTimeout = null;
    this.activeScrollingText = null;
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onResize = this.handleResize.bind(this);
  }

  AnnouncementSlider.prototype.getVisibleTextElement = function (slide) {
    if (!slide) return null;
    const candidates = Array.prototype.slice.call(slide.querySelectorAll('.announcement-bar-text, .announcement-bar-text-mobile'));
    for (let i = 0; i < candidates.length; i += 1) {
      const el = candidates[i];
      if (window.getComputedStyle(el).display !== 'none') {
        return el;
      }
    }
    return null;
  };

  AnnouncementSlider.prototype.getOrCreateScrollTrack = function (textEl) {
    if (!textEl) return null;
    let track = textEl.querySelector('.announcement-scroll-track');
    if (track) return track;

    track = document.createElement('span');
    track.className = 'announcement-scroll-track';
    while (textEl.firstChild) {
      track.appendChild(textEl.firstChild);
    }
    textEl.appendChild(track);
    return track;
  };

  AnnouncementSlider.prototype.stopActiveTextScroll = function () {
    if (this.scrollResumeTimeout) {
      window.clearTimeout(this.scrollResumeTimeout);
      this.scrollResumeTimeout = null;
    }

    if (!this.activeScrollingText) return;

    this.activeScrollingText.classList.remove('is-scrolling-left');
    this.activeScrollingText.style.removeProperty('--announcement-scroll-distance');
    this.activeScrollingText.style.removeProperty('--announcement-scroll-duration');
    this.activeScrollingText.style.removeProperty('--announcement-scroll-delay');
    this.activeScrollingText = null;
  };

  AnnouncementSlider.prototype.setupActiveSlideTextScroll = function () {
    this.stopActiveTextScroll();
    if (!this.slides.length) return;

    const activeSlide = this.slides[this.activeIndex];
    const textEl = this.getVisibleTextElement(activeSlide);
    if (!textEl) {
      this.resume();
      return;
    }

    const trackEl = this.getOrCreateScrollTrack(textEl);
    if (!trackEl) {
      this.resume();
      return;
    }

    const overflowDistance = trackEl.scrollWidth - textEl.clientWidth;
    if (overflowDistance <= 0) {
      this.resume();
      return;
    }

    textEl.classList.add('is-scrolling-left');
    textEl.style.setProperty('--announcement-scroll-distance', `${overflowDistance}px`);
    textEl.style.setProperty('--announcement-scroll-delay', `${this.scrollCycleDelayMs}ms`);

    const travelMs = Math.max(2500, Math.round((overflowDistance / this.scrollPxPerSecond) * 1000));
    textEl.style.setProperty('--announcement-scroll-duration', `${travelMs}ms`);
    this.activeScrollingText = textEl;

    this.pause();

    if (this.slides.length > 1) {
      this.scrollResumeTimeout = window.setTimeout(() => {
        this.stopActiveTextScroll();
        this.next();
        this.start();
      }, travelMs + this.scrollCycleDelayMs * 2);
    }
  };

  AnnouncementSlider.prototype.parseDate = function (value) {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  };

  AnnouncementSlider.prototype.getLocationHints = function () {
    const hints = {
      countryIso: this.countryIso,
      market: this.countryIso === 'gb' ? 'uk' : this.countryIso ? 'international' : ''
    };

    if (window.AdvancedAnnouncementLocation && typeof window.AdvancedAnnouncementLocation === 'object') {
      if (window.AdvancedAnnouncementLocation.market) {
        hints.market = String(window.AdvancedAnnouncementLocation.market).toLowerCase();
      }
      if (window.AdvancedAnnouncementLocation.countryIso) {
        hints.countryIso = String(window.AdvancedAnnouncementLocation.countryIso).toLowerCase();
      }
    }
    return hints;
  };

  AnnouncementSlider.prototype.fetchBrowsingContextCountry = function () {
    return window.fetch('/browsing_context_suggestions.json', {
      credentials: 'same-origin'
    }).then((response) => {
      if (!response.ok) return null;
      return response.json();
    }).then((payload) => {
      const handle = payload && payload.detected_values && payload.detected_values.country && payload.detected_values.country.handle;

      if (handle) {
        this.detectedCountryHandle = String(handle).toUpperCase();
      }
    }).catch(() => {
      // Keep fallback country handle when request fails.
    });
  };

  AnnouncementSlider.prototype.matchesLocationRule = function (rule, targetCountryHandle) {
    if (!rule || rule === 'all') return true;

    const hints = this.getLocationHints();

    if (rule === 'uk') {
      return hints.countryIso === 'gb' || hints.market === 'uk';
    }

    if (rule === 'international') {
      if (hints.market) return hints.market === 'international';
      return hints.countryIso ? hints.countryIso !== 'gb' : false;
    }

    if (rule === 'country_handle') {
      if (!targetCountryHandle) return false;
      return String(this.detectedCountryHandle || '').toUpperCase() === String(targetCountryHandle).toUpperCase();
    }

    return false;
  };

  AnnouncementSlider.prototype.matchesSchedule = function (startValue, endValue) {
    if (!startValue && !endValue) return true;

    const now = new Date();
    const start = this.parseDate(startValue);
    const end = this.parseDate(endValue);

    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  };

  AnnouncementSlider.prototype.isMobileViewport = function () {
    return window.matchMedia('(max-width: 767px)').matches;
  };

  AnnouncementSlider.prototype.matchesDeviceRule = function (rule) {
    if (!rule || rule === 'all') return true;
    const isMobile = this.isMobileViewport();
    if (rule === 'mobile_only') return isMobile;
    if (rule === 'desktop_only') return !isMobile;
    return true;
  };

  AnnouncementSlider.prototype.applyJsVisibilityRules = function () {
    const visibleSlides = [];

    for (let i = 0; i < this.allSlides.length; i += 1) {
      const slide = this.allSlides[i];
      const needsJsVisibility = slide.dataset.requiresJsVisibility === 'true';
      let shouldShow = true;

      if (needsJsVisibility) {
        shouldShow = this.matchesLocationRule(slide.dataset.locationRule, slide.dataset.countryHandle) && this.matchesSchedule(slide.dataset.scheduleStart, slide.dataset.scheduleEnd);
      }

      shouldShow = shouldShow && this.matchesDeviceRule(slide.dataset.deviceVisibility);

      if (shouldShow) {
        visibleSlides.push(slide);
      } else {
        slide.hidden = true;
        slide.classList.remove('is-active');
      }
    }

    this.slides = visibleSlides;
  };

  AnnouncementSlider.prototype.showSlide = function (index, animate) {
    if (index < 0 || index >= this.slides.length) return;
    if (index === this.activeIndex && animate) return;

    if (!animate || this.slides.length <= 1) {
      for (let i = 0; i < this.slides.length; i += 1) {
        const isActive = i === index;
        this.slides[i].hidden = !isActive;
        this.slides[i].setAttribute('aria-hidden', isActive ? 'false' : 'true');
        this.slides[i].classList.toggle('is-active', isActive);
        this.slides[i].classList.remove('is-pre-enter-up');
        this.slides[i].classList.remove('is-entering-up');
        this.slides[i].classList.remove('is-leaving-up');
      }
      this.activeIndex = index;
      this.setupActiveSlideTextScroll();
      return;
    }

    if (this.isAnimating) return;

    const currentSlide = this.slides[this.activeIndex];
    const nextSlide = this.slides[index];
    if (!currentSlide || !nextSlide) return;

    this.isAnimating = true;
    currentSlide.hidden = false;
    nextSlide.hidden = false;
    nextSlide.classList.remove('is-leaving-up');
    nextSlide.classList.remove('is-entering-up');
    nextSlide.classList.remove('is-active');
    nextSlide.classList.add('is-pre-enter-up');
    nextSlide.setAttribute('aria-hidden', 'false');

    // Force browser to apply the pre-enter state before transition starts.
    nextSlide.offsetHeight;

    window.requestAnimationFrame(() => {
      nextSlide.classList.add('is-active');
      nextSlide.classList.add('is-entering-up');
      nextSlide.classList.remove('is-pre-enter-up');
      currentSlide.classList.add('is-leaving-up');

      window.setTimeout(() => {
        // Prevent a visible "drop back down" when the previous slide resets.
        currentSlide.style.transition = 'none';
        currentSlide.classList.remove('is-active');
        currentSlide.classList.remove('is-leaving-up');
        currentSlide.setAttribute('aria-hidden', 'true');
        currentSlide.hidden = true;
        nextSlide.classList.remove('is-entering-up');
        nextSlide.hidden = false;

        // Force reflow so the no-transition reset is applied, then restore CSS transitions.
        currentSlide.offsetHeight;
        currentSlide.style.transition = '';

        this.activeIndex = index;
        this.isAnimating = false;
        this.setupActiveSlideTextScroll();
      }, this.transitionMs);
    });
  };

  AnnouncementSlider.prototype.next = function () {
    let nextIndex = this.activeIndex + 1;
    if (nextIndex >= this.slides.length) {
      nextIndex = 0;
    }
    this.showSlide(nextIndex, true);
  };

  AnnouncementSlider.prototype.refreshVisibleSlides = function () {
    this.applyJsVisibilityRules();
    this.stopActiveTextScroll();

    if (!this.slides.length) {
      this.pause();
      this.section.hidden = true;
      this.sliderEl.style.minHeight = '';
      return;
    }

    this.section.hidden = false;

    if (this.activeIndex >= this.slides.length) {
      this.activeIndex = 0;
    }

    this.showSlide(this.activeIndex, false);
    this.updateHeight();
    this.pause();
    this.start();
  };

  AnnouncementSlider.prototype.updateHeight = function () {
    if (!this.sliderEl) return;
    // Keep a stable baseline height to avoid load-time layout shifts.
    this.sliderEl.style.minHeight = '20px';
  };

  AnnouncementSlider.prototype.handleResize = function () {
    this.refreshVisibleSlides();
  };

  AnnouncementSlider.prototype.start = function () {
    if (!this.enableAutoplay || this.autoplayMs <= 0 || this.slides.length <= 1) return;
    if (this.timer) return;
    this.timer = window.setInterval(() => {
      this.next();
    }, this.autoplayMs);
  };

  AnnouncementSlider.prototype.pause = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  };

  AnnouncementSlider.prototype.resume = function () {
    if (document.hidden) return;
    if (this.activeScrollingText) return;
    this.start();
  };

  AnnouncementSlider.prototype.handleVisibilityChange = function () {
    if (document.hidden) {
      this.pause();
      return;
    }
    this.resume();
  };

  AnnouncementSlider.prototype.mount = function () {
    if (!this.sliderEl || !this.allSlides.length) return false;

    let initialVisibleIndex = 0;
    this.applyJsVisibilityRules();
    for (let i = 0; i < this.slides.length; i += 1) {
      if (this.slides[i].classList.contains('is-active')) {
        initialVisibleIndex = i;
        break;
      }
    }
    this.activeIndex = initialVisibleIndex;

    this.refreshVisibleSlides();
    this.fetchBrowsingContextCountry().then(this.refreshVisibleSlides.bind(this));
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.onResize);
    return true;
  };

  AnnouncementSlider.prototype.unmount = function () {
    this.stopActiveTextScroll();
    this.pause();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onResize);
  };

  function initSection(section) {
    if (!section) return;
    if (section.dataset.advancedAnnouncementInitialized === 'true') return;
    if (section.dataset.advancedAnnouncementInitializing === 'true') return;
    section.dataset.advancedAnnouncementInitializing = 'true';

    const sliderId = section.dataset.sectionId || section.getAttribute('data-section-id') || 'global';
    const instance = new AnnouncementSlider(section);
    const didMount = instance.mount();

    if (!didMount) {
      section.remove();
      return;
    }

    instances[sliderId] = instance;
    section.dataset.advancedAnnouncementInitialized = 'true';
    section.dataset.advancedAnnouncementInitializing = 'false';
    section.classList.remove('is-loading');
  }

  function destroySection(section) {
    if (!section) return;
    const sliderId = section.dataset.sectionId || section.getAttribute('data-section-id') || 'global';
    if (instances[sliderId]) {
      instances[sliderId].unmount();
      delete instances[sliderId];
    }
    section.dataset.advancedAnnouncementInitialized = 'false';
    section.dataset.advancedAnnouncementInitializing = 'false';
  }

  function initAll() {
    const sections = document.querySelectorAll(SECTION_SELECTOR);
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', () => {
    initAll();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;

    if (event.target.matches && event.target.matches(SECTION_SELECTOR)) {
      initSection(event.target);
      return;
    }

    initSection(event.target.querySelector ? event.target.querySelector(SECTION_SELECTOR) : null);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;

    if (event.target.matches && event.target.matches(SECTION_SELECTOR)) {
      destroySection(event.target);
      return;
    }

    destroySection(event.target.querySelector ? event.target.querySelector(SECTION_SELECTOR) : null);
  });
})();
